import { useEffect, useState } from "react";
import { InvoiceLinesAssignment } from "../components/InvoiceLinesAssignment";
import { InvoiceEntryLines } from "../components/InvoiceEntryLines";
import { DeadlineBadge } from "../components/DeadlineBadge";
import { submitInvoiceToControl, type CodingWorkflow } from "../api/claimsApi";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Pencil,
  PlayCircle,
  Printer,
  Send,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { useEnterpriseInvoiceDetail } from "../hooks/useEnterpriseInvoiceDetail";
import { downloadCompanyBillingAttachment } from "../api/companyApi";
import {
  downloadInvoiceFile,
  getInvoiceFilePreviewUrl,
  openInvoiceFile,
  printInvoiceFile,
} from "../api/invoiceFileApi";
import { openInvoicePrint, openInvoiceForfaitReport, openInvoiceAiReport } from "../api/invoicePrintApi";
import { getAuthToken, isSuperAdminUser } from "../auth";
import { reopenAdminInvoice } from "../api/adminApi";
import { cn } from "@/shared/lib/cn";
import { companyBillingListPath } from "../lib/companyBillingPaths";
import { workContextHeaders } from "@/modules/safecheck/workContext";

type EnterpriseInvoiceDetailPageProps = {
  displayId: number;
  scope?: "company" | "hospital" | "auditor" | "admin";
};

function formatMoney(amount: number, currency: "USD" | "CDF" = "USD"): string {
  const decimals = currency === "CDF" ? 0 : 2;
  const suffix = currency === "CDF" ? " FC" : " USD";
  return `${amount.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
}

function formatRate(rate: number): string {
  return `${rate.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} FC/USD`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString("fr-FR")} à ${date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function formatFileSize(bytes: number): string {
  if (bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function EnterpriseInvoiceDetailPage({
  displayId,
  scope = "company",
}: EnterpriseInvoiceDetailPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.enterpriseDetail;
  const listLabels = t.safecheck.invoices.enterpriseAll;
  const { printPage, showToast } = usePageActions();
  const { invoice, loading, acting, error, usingApi, startReview, applyExchangeRate, approve, reject, reload } =
    useEnterpriseInvoiceDetail(displayId, scope);
  // Le compte Assurance ne voit pas les codes actes.
  const hideActCodes = scope === "company";
  const [auditNotes, setAuditNotes] = useState("");
  // Circuit : codification Audit → contrôle Sinistre → validation finale Audit.
  const [workflow, setWorkflow] = useState<CodingWorkflow | null>(null);
  const [linesRefresh, setLinesRefresh] = useState(0);
  const [transmitting, setTransmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [montantApprouveInput, setMontantApprouveInput] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rateInput, setRateInput] = useState("");
  const [rateMessage, setRateMessage] = useState<string | null>(null);

  const homeHref =
    scope === "auditor"
      ? "/dashboard/auditor"
      : scope === "hospital"
        ? "/dashboard/hospital"
        : scope === "admin"
          ? "/dashboard/admin"
          : "/dashboard/enterprise";

  const [previewAttachmentId, setPreviewAttachmentId] = useState<number | null>(null);

  useEffect(() => {
    if (!invoice) return;
    if (invoice.auditNotes) setAuditNotes(invoice.auditNotes);
    if (invoice.rejectionReason) setRejectionReason(invoice.rejectionReason);
  }, [invoice?.displayId, invoice?.auditNotes, invoice?.rejectionReason]);

  // Les relevés entreprise ne sont plus utilisés : l'assureur consulte les factures prestataires.
  const isCompanyBilling = false;
  const billingAttachments = invoice?.attachments ?? [];
  const hasBillingAttachments = isCompanyBilling && billingAttachments.length > 0;
  const hasProviderAttachment =
    !isCompanyBilling && Boolean(invoice?.fileName && invoice.fileName !== "—");
  const hasAttachment = hasBillingAttachments || hasProviderAttachment;

  useEffect(() => {
    if (!invoice || !hasAttachment || !getAuthToken() || !usingApi) {
      setPreviewUrl(null);
      setPreviewError(null);
      setPreviewAttachmentId(null);
      return;
    }

    if (hasBillingAttachments) {
      const previewable =
        billingAttachments.find(
          (att) =>
            att.mime_type.includes("pdf") ||
            att.mime_type.includes("image") ||
            att.original_name.match(/\.(pdf|jpe?g|png)$/i),
        ) ?? billingAttachments[0];

      let cancelled = false;
      let objectUrl: string | null = null;

      void (async () => {
        try {
          const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
          const token = getAuthToken();
          const response = await fetch(
            `${getApiBaseUrl()}/insurer/billing/attachments/${previewable.id}/download`,
            { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workContextHeaders() } },
          );
          if (!response.ok) {
            throw new Error("Aperçu indisponible.");
          }
          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setPreviewUrl(objectUrl);
          setPreviewAttachmentId(previewable.id);
          setPreviewError(null);
        } catch (err) {
          if (!cancelled) {
            setPreviewUrl(null);
            setPreviewAttachmentId(null);
            setPreviewError(err instanceof Error ? err.message : "Aperçu indisponible.");
          }
        }
      })();

      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    void (async () => {
      try {
        objectUrl = await getInvoiceFilePreviewUrl(displayId);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl(objectUrl);
        setPreviewError(null);
      } catch (err) {
        if (!cancelled) {
          setPreviewUrl(null);
          setPreviewError(err instanceof Error ? err.message : "Aperçu indisponible.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [billingAttachments, displayId, hasAttachment, hasBillingAttachments, invoice, usingApi]);

  const isForfaitMensuel =
    invoice?.isForfaitMensuel === true || invoice?.invoiceNature === "forfait_mensuel";

  const handleForfaitReport = async (pricing: "prestataire" | "client") => {
    if (!getAuthToken() || !usingApi) return;
    setPrinting(true);
    try {
      await openInvoiceForfaitReport(displayId, pricing);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impression impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setPrinting(false);
    }
  };

  const handleAiReport = async (mode: "revised" | "complete") => {
    if (!getAuthToken() || !usingApi) return;
    setPrinting(true);
    try {
      await openInvoiceAiReport(displayId, mode);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Génération impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="theme-card p-8 text-center text-sm text-muted">
        Chargement de la facture…
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">
          {error ?? (scope === "company" ? labels.billingNotFound : labels.notFound)}
        </p>
        <Link
          to={
            scope === "auditor"
              ? "/dashboard/invoices/pending"
              : scope === "company"
                ? companyBillingListPath()
                : "/dashboard/invoices"
          }
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={14} />
          {labels.back}
        </Link>
      </div>
    );
  }

  const statusLabel =
    listLabels.statuses[invoice.status as keyof typeof listLabels.statuses] ?? invoice.status;
  const treatmentLabel = labels.treatmentTypes[invoice.treatmentType];
  // Plus de facturation au forfait dans le circuit Assurance : la nature n'est plus affichée.
  const natureLabel: string | null = null;
  const legacy = (invoice.legacyStatus || "").toLowerCase();
  const canStartReview = scope === "auditor" && legacy === "submitted";
  const canDecide =
    scope === "auditor" &&
    ["submitted", "under review", "auditstarted"].includes(legacy);
  const canEditInvoice = scope === "admin" && invoice.canEdit === true;
  const isApproved = legacy === "approved";
  const isRejected = legacy === "rejected";
  const currency = invoice.invoiceCurrency === "CDF" ? "CDF" : "USD";
  const showExchangeCard =
    (scope === "auditor" || scope === "admin") &&
    (currency === "CDF" ||
      invoice.hospitalUsesCdf === true ||
      (invoice.hospitalRateToday != null && invoice.hospitalRateToday > 0) ||
      (invoice.exchangeRateCdf != null && invoice.exchangeRateCdf > 0));

  const handleStartReview = async () => {
    const ok = await startReview();
    if (ok) showToast(t.actions.saved);
  };

  const handleApplyExchangeRate = async () => {
    setRateMessage(null);
    const raw = rateInput.trim().replace(/\s/g, "").replace(",", ".");
    const custom = raw !== "" ? Number(raw) : null;
    if (custom != null && (Number.isNaN(custom) || custom < 100 || custom > 50000)) {
      setRateMessage("Taux invalide (100 à 50 000 FC/USD).");
      return;
    }
    const ok = await applyExchangeRate(custom);
    if (ok) {
      setRateMessage("Taux enregistré sur cette facture.");
      setRateInput("");
      showToast(t.actions.saved);
    }
  };

  const handleApprove = async () => {
    if (workflow && !workflow.can_approve) {
      showToast({ message: "La facture doit d'abord être contrôlée par le Sinistre.", variant: "warning" });
      return;
    }
    if (auditNotes.trim() !== "" && auditNotes.trim().length < 10) {
      showToast({ message: "La note de synthèse doit faire au moins 10 caractères.", variant: "warning" });
      return;
    }
    if (!window.confirm("Confirmer la validation finale de cette facture ?")) return;

    const raw = montantApprouveInput.trim().replace(/\s/g, "").replace(",", ".");
    const parsed = raw !== "" ? Number(raw) : undefined;
    const ok = await approve({
      audit_notes: auditNotes.trim() || undefined,
      montant_approuve: parsed != null && !Number.isNaN(parsed) ? parsed : undefined,
    });
    if (ok) showToast(t.actions.saved);
  };

  const handleTransmit = async () => {
    if (auditNotes.trim().length < 10) {
      showToast({
        message: "Rédigez la note de synthèse (10 caractères minimum) : elle est transmise au Sinistre.",
        variant: "warning",
      });
      return;
    }
    setTransmitting(true);
    try {
      await submitInvoiceToControl(Number(displayId), auditNotes.trim());
      setLinesRefresh((value) => value + 1);
      await reload();
      showToast("Facture transmise au Sinistre.");
    } catch (err) {
      showToast({ message: err instanceof Error ? err.message : "Transmission impossible.", variant: "warning" });
    } finally {
      setTransmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    const ok = await reject({
      rejection_reason: rejectionReason.trim(),
      audit_notes: auditNotes.trim() || undefined,
    });
    if (ok) showToast(t.actions.saved);
  };

  const handlePrint = async () => {
    if (!getAuthToken() || !usingApi) {
      printPage();
      return;
    }

    setPrinting(true);
    try {
      if (hasBillingAttachments && previewUrl) {
        const printWindow = window.open(previewUrl, "_blank");
        printWindow?.focus();
        printWindow?.print();
      } else if (hasAttachment) {
        await printInvoiceFile(displayId);
      } else {
        await openInvoicePrint(displayId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impression impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setPrinting(false);
    }
  };

  const handleDownload = async (attachmentId?: number, fileName?: string) => {
    if (!hasAttachment) return;
    setDownloading(true);
    try {
      if (hasBillingAttachments) {
        const target =
          billingAttachments.find((att) => att.id === attachmentId) ?? billingAttachments[0];
        await downloadCompanyBillingAttachment(target.id, fileName ?? target.original_name);
      } else {
        await downloadInvoiceFile(displayId, invoice.fileName);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Téléchargement impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenFile = async () => {
    if (!hasAttachment) return;
    try {
      if (hasBillingAttachments) {
        const target =
          billingAttachments.find((att) => att.id === previewAttachmentId) ??
          billingAttachments[0];
        await downloadCompanyBillingAttachment(target.id, target.original_name);
      } else {
        await openInvoiceFile(displayId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ouverture du document impossible.";
      showToast({ message, variant: "warning" });
    }
  };

  return (
    <>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}

      {isCompanyBilling && (
        <div className="mb-4 rounded-xl border border-primary/20 bg-primary-soft/40 px-4 py-3 text-sm text-foreground">
          {labels.billingCompanyNotice}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            {
              label: t.safecheck.nav.invoices,
              href:
                scope === "auditor"
                  ? "/dashboard/invoices/pending"
                  : scope === "company"
                    ? companyBillingListPath()
                    : "/dashboard/invoices",
            },
            {
              label: (isCompanyBilling ? labels.billingPageTitle : labels.pageTitle).replace(
                "{id}",
                String(invoice.displayId),
              ),
            },
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          {canEditInvoice && (
            <Link
              to={`/dashboard/invoices/${displayId}/edit`}
              className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent/20 hover:border-accent"
            >
              <Pencil size={15} />
              Modifier
            </Link>
          )}
          {!isCompanyBilling && (
          <>
          <button
            type="button"
            disabled={!hasAttachment || downloading}
            onClick={() => void handleDownload()}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border border-pink/30 px-3 py-2 text-sm font-medium text-pink transition-colors",
              hasAttachment
                ? "hover:bg-pink/10"
                : "cursor-not-allowed opacity-60",
            )}
          >
            <Download size={15} />
            {downloading ? "Téléchargement…" : labels.download}
          </button>
          <button
            type="button"
            disabled={printing}
            onClick={() => void handlePrint()}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
          >
            <Printer size={15} />
            {printing ? "Ouverture…" : labels.print}
          </button>
          </>
          )}
          {isForfaitMensuel && usingApi && scope !== "company" && (
            <>
              <button
                type="button"
                disabled={printing}
                onClick={() => void handleForfaitReport("prestataire")}
                className="inline-flex items-center gap-2 rounded-lg border border-teal/30 bg-teal-soft px-3 py-2 text-sm font-medium text-teal transition-colors hover:bg-teal/10 disabled:opacity-60"
              >
                <Printer size={15} />
                Facture prestataire
              </button>
              {invoice.forfaitHasClientRate && (
                <button
                  type="button"
                  disabled={printing}
                  onClick={() => void handleForfaitReport("client")}
                  className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
                >
                  <Printer size={15} />
                  Facture client
                </button>
              )}
            </>
          )}
          {!isForfaitMensuel && invoice.hasAiAnalysis && usingApi && scope !== "company" && (
            <>
              <button
                type="button"
                disabled={printing}
                onClick={() => void handleAiReport("revised")}
                className="inline-flex items-center gap-2 rounded-lg border border-green-600/30 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 disabled:opacity-60"
              >
                <Printer size={15} />
                Facture révisée
              </button>
              <button
                type="button"
                disabled={printing}
                onClick={() => void handleAiReport("complete")}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-600/30 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-60"
              >
                <Printer size={15} />
                Facture complète
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() =>
              navigate(
                scope === "auditor"
                  ? "/dashboard/invoices/pending"
                  : scope === "company"
                    ? companyBillingListPath()
                    : "/dashboard/invoices",
              )
            }
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
          >
            <ArrowLeft size={15} />
            {labels.back}
          </button>
        </div>
      </div>

      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-foreground">
          {labels.pageTitle.replace("{id}", String(invoice.displayId))}
        </h1>
        <div className="mt-3 flex flex-wrap gap-2">
          <span
            title={labels.glossary.status}
            className="inline-flex cursor-help rounded-md bg-primary-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary"
          >
            {statusLabel}
          </span>
          <span
            title={labels.glossary.treatment}
            className="inline-flex cursor-help rounded-md bg-card-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {treatmentLabel}
          </span>
          {natureLabel && (
            <span
              title={labels.glossary.nature}
              className="inline-flex cursor-help rounded-md bg-teal-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-teal"
            >
              {natureLabel}
            </span>
          )}
        </div>
      </div>

      {(isApproved || isRejected) && (
        <section
          className={cn(
            "theme-card mb-5 overflow-hidden border-l-4",
            isApproved ? "border-l-green" : "border-l-red-600",
          )}
        >
          <div
            className={cn(
              "px-5 py-3 text-sm font-semibold text-white",
              isApproved ? "bg-green" : "bg-red-600",
            )}
          >
            {isApproved ? "Approuvée par l'auditeur" : "Rejetée par l'auditeur"}
          </div>
          <dl className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            {invoice.validatedByName && (
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Auditeur</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">{invoice.validatedByName}</dd>
              </div>
            )}
            {invoice.validatedAt && (
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Date</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">
                  {formatDateTime(invoice.validatedAt)}
                </dd>
              </div>
            )}
            {isApproved && invoice.montantApprouve != null && invoice.montantApprouve > 0 && (
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  Montant approuvé
                </dt>
                <dd className="mt-1 text-lg font-semibold text-green-700">
                  {formatMoney(invoice.montantApprouve, currency)}
                </dd>
                {invoice.montantApprouve < invoice.totalAmount && (
                  <p className="mt-1 text-xs text-amber-700">
                    Réduction de {formatMoney(invoice.totalAmount - invoice.montantApprouve, currency)}
                  </p>
                )}
              </div>
            )}
            {invoice.auditNotes && (
              <div className="sm:col-span-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  Notes d&apos;audit
                </dt>
                <dd className="mt-1 text-sm text-foreground">{invoice.auditNotes}</dd>
              </div>
            )}
          </dl>
          {isRejected && invoice.rejectionReason && (
            <div className="mx-5 mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <strong>Raison du rejet :</strong> {invoice.rejectionReason}
            </div>
          )}
        </section>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          {scope === "auditor" && usingApi ? (
            <>
              {/* L'Admin soumet la facture ; l'auditeur lance le traitement (lecture IA) ou saisit les lignes en secours. */}
              <InvoiceEntryLines invoiceId={Number(displayId)} scope="auditor" onSaved={() => setLinesRefresh((value) => value + 1)} />
              <InvoiceLinesAssignment invoiceId={Number(displayId)} refreshKey={linesRefresh} onWorkflowChange={setWorkflow} />
            </>
          ) : null}
          {scope === "admin" && usingApi ? <InvoiceEntryLines invoiceId={Number(displayId)} /> : null}
          <section className="theme-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <FileText size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-foreground">{labels.sections.info}</h2>
            </div>
            <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.patientEmployee}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{invoice.patientEmployee ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.company}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{invoice.company}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.hospital}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{invoice.hospital}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.treatmentType}
                </dt>
                <dd className="mt-1">
                  <span
                    title={labels.glossary.treatment}
                    className="inline-flex cursor-help rounded-md bg-card-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {treatmentLabel}
                  </span>
                </dd>
              </div>
              {natureLabel && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.fields.invoiceNature}
                  </dt>
                  <dd className="mt-1">
                    <span
                      title={labels.glossary.nature}
                      className="inline-flex cursor-help rounded-md bg-teal-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal"
                    >
                      {natureLabel}
                    </span>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.providerReference}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-pink">{invoice.providerReference}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.period}
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {invoice.periodStart} → {invoice.periodEnd}
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.submissionNotes}
                </dt>
                <dd className="mt-1 rounded-lg bg-card-muted/40 p-3 text-sm leading-relaxed text-foreground">
                  {invoice.submissionNotes}
                </dd>
              </div>
            </dl>
          </section>

          <section className="theme-card overflow-hidden">
            <div className="flex items-center gap-2 border-b border-border px-5 py-4">
              <ClipboardList size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-foreground">{labels.sections.acts}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-border bg-card-muted/50">
                    {labels.actColumns.slice(hideActCodes ? 1 : 0).map((column) => (
                      <th
                        key={column}
                        className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoice.medicalActs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                        {invoice.invoiceNature === "forfait_mensuel" ||
                        invoice.treatmentType === "forfaitaire"
                          ? labels.emptyActsForfait
                          : labels.emptyActs}
                      </td>
                    </tr>
                  ) : (
                    invoice.medicalActs.map((act) => (
                      <tr key={act.id} className="border-b border-border last:border-0">
                        {hideActCodes ? null : <td className="px-4 py-3 text-sm text-foreground">{act.code}</td>}
                        <td className="px-4 py-3 text-sm text-foreground">{act.label}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{formatMoney(act.cost, currency)}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{act.date}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-primary-soft/50">
                    <td
                      colSpan={hideActCodes ? 2 : 3}
                      className="px-4 py-3 text-sm font-semibold uppercase tracking-wide text-primary"
                    >
                      {labels.totalBilled}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-primary">
                      {formatMoney(invoice.totalAmount, currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {!isCompanyBilling && (
          <section className="theme-card p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">
                  {labels.sections.document}
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!hasAttachment}
                  onClick={() => void handleOpenFile()}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground",
                    hasAttachment ? "hover:bg-accent/20" : "cursor-not-allowed opacity-60",
                  )}
                >
                  <ExternalLink size={15} />
                  Ouvrir
                </button>
                <button
                  type="button"
                  disabled={!hasAttachment || downloading}
                  onClick={() => void handleDownload()}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg border border-pink/30 px-3 py-2 text-sm font-medium text-pink",
                    hasAttachment ? "hover:bg-pink/10" : "cursor-not-allowed opacity-60",
                  )}
                >
                  <Download size={15} />
                  {downloading ? "…" : labels.download}
                </button>
              </div>
            </div>

            {!hasAttachment ? (
              <div className="flex min-h-[180px] items-center justify-center rounded-xl border border-dashed border-border bg-card-muted/30 p-6 text-center">
                <p className="text-sm text-muted">{labels.noAttachments}</p>
              </div>
            ) : previewUrl ? (
              <iframe
                title={invoice.fileName}
                src={previewUrl}
                className="h-[480px] w-full rounded-xl border border-border bg-white"
              />
            ) : (
              <div className="flex min-h-[180px] items-center justify-center rounded-xl border border-dashed border-border bg-card-muted/30 p-6 text-center">
                <div>
                  <FileText size={36} className="mx-auto text-muted" />
                  <p className="mt-3 text-sm font-medium text-foreground">{invoice.fileName}</p>
                  <p className="mt-1 text-xs text-muted">
                    {previewError ?? labels.documentHint}
                  </p>
                </div>
              </div>
            )}
          </section>
          )}
        </div>

        <aside className="space-y-5">
          <div className="theme-card h-fit p-5">
            <div className="mb-4 flex items-center gap-2">
              <Clock3 size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-foreground">{labels.sections.tracking}</h2>
            </div>
            <dl className="space-y-4">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.number}
                </dt>
                <dd className="mt-1 text-lg font-semibold text-pink">#{invoice.displayId}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.submittedAt}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{invoice.submittedAt}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Montant facturé</dt>
                <dd className="mt-1 text-lg font-bold text-primary">{formatMoney(invoice.amounts?.billed ?? invoice.totalAmount, currency)}</dd>
              </div>
              {invoice.amounts ? (
                <>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Cumul calculé</dt>
                    <dd className="mt-1 text-sm font-semibold text-foreground">
                      {invoice.amounts.lines_total !== null ? formatMoney(invoice.amounts.lines_total, currency) : "—"}
                    </dd>
                    <p className="mt-1 text-[11px] text-muted">
                      {invoice.amounts.lines_total === null
                        ? "Aucune ligne saisie."
                        : Math.abs(invoice.amounts.lines_total - invoice.amounts.billed) > 0.01
                          ? `${invoice.amounts.lines_count} ligne(s) · écart ${formatMoney(invoice.amounts.billed - invoice.amounts.lines_total, currency)} avec le montant facturé`
                          : `${invoice.amounts.lines_count} ligne(s) · concordant`}
                    </p>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Montant validé</dt>
                    <dd className="mt-1 text-sm font-semibold text-foreground">
                      {invoice.amounts.validated !== null ? formatMoney(invoice.amounts.validated, currency) : "Non validée"}
                    </dd>
                    {invoice.amounts.validated === null && invoice.amounts.covered !== null ? (
                      <p className="mt-1 text-[11px] text-muted">Pris en charge à ce stade : {formatMoney(invoice.amounts.covered, currency)}</p>
                    ) : null}
                  </div>
                </>
              ) : null}
              {scope === "admin" && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.fields.coveredAmount}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-foreground">
                    {formatMoney(invoice.coveredAmount, currency)}
                  </dd>
                  <p className="mt-1 text-[11px] text-muted">{labels.fields.coveredAmountHint}</p>
                </div>
              )}
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Devise</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">{currency}</dd>
                {currency === "CDF" && invoice.exchangeRateCdf != null && invoice.exchangeRateCdf > 0 && (
                  <p className="mt-1 text-[11px] text-muted">
                    Taux facture : {formatRate(invoice.exchangeRateCdf)}
                  </p>
                )}
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.fields.currentStatus}
                </dt>
                <dd className="mt-1">
                  <span className="inline-flex rounded-md bg-primary-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    {statusLabel}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Niveau en cours et échéance</dt>
                <dd className="mt-1">
                  <DeadlineBadge deadline={invoice.deadline} />
                </dd>
              </div>
              {(invoice.reopenings ?? []).length > 0 ? (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Réouvertures (IT)</dt>
                  <dd className="mt-1 space-y-1 text-xs text-foreground">
                    {(invoice.reopenings ?? []).map((item) => (
                      <p key={item.at}>
                        {formatDateTime(item.at)} par {item.by} : {item.reason}
                      </p>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>

          {scope === "admin" && usingApi && isApproved && isSuperAdminUser() ? (
            <div className="theme-card h-fit space-y-3 border border-amber-200 p-5">
              <h2 className="text-base font-semibold text-foreground">Rouvrir la validation (IT)</h2>
              <p className="text-xs text-muted">
                La facture revient chez le médecin pour la codification, puis repasse par la validation du Sinistre.
                Le motif est conservé dans l'historique.
              </p>
              <FormField label="Motif de la réouverture" required>
                <textarea
                  value={reopenReason}
                  onChange={(event) => setReopenReason(event.target.value)}
                  className={`${inputClass} min-h-[70px]`}
                  placeholder="Ex. correction demandée par l'assureur sur le patient de la ligne 3"
                />
              </FormField>
              <button
                type="button"
                disabled={reopening || reopenReason.trim().length < 10}
                onClick={() =>
                  void (async () => {
                    setReopening(true);
                    try {
                      await reopenAdminInvoice(Number(displayId), reopenReason.trim());
                      setReopenReason("");
                      showToast("Facture rouverte : elle revient chez le médecin.");
                      await reload();
                    } catch (err) {
                      showToast({ message: err instanceof Error ? err.message : "Réouverture impossible.", variant: "warning" });
                    } finally {
                      setReopening(false);
                    }
                  })()
                }
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-600/90 disabled:opacity-60"
              >
                Rouvrir la validation
              </button>
            </div>
          ) : null}

          {showExchangeCard && (
            <div className="theme-card h-fit space-y-3 border border-green-200 p-5">
              <h2 className="text-base font-semibold text-foreground">Taux CDF → USD</h2>
              {invoice.exchangeRateCdf != null && invoice.exchangeRateCdf > 0 ? (
                <p className="text-sm text-foreground">
                  Taux appliqué : <strong>{formatRate(invoice.exchangeRateCdf)}</strong>
                </p>
              ) : (
                <p className="text-sm text-amber-800">
                  Aucun taux sur cette facture — requis pour l&apos;analyse IA si devise CDF.
                </p>
              )}
              {invoice.hospitalRateToday != null && invoice.hospitalRateToday > 0 ? (
                <p className="text-xs text-muted">
                  Taux du jour (hôpital) : <strong>{formatRate(invoice.hospitalRateToday)}</strong>
                </p>
              ) : (
                <p className="text-xs text-amber-800">
                  Aucun taux hôpital configuré — renseignez-le dans Taux CDF / USD (admin).
                </p>
              )}
              <FormField label="Taux FC / 1 USD (optionnel)">
                <input
                  type="number"
                  min={100}
                  max={50000}
                  step="0.01"
                  value={rateInput}
                  onChange={(event) => setRateInput(event.target.value)}
                  placeholder={
                    invoice.hospitalRateToday != null
                      ? String(invoice.hospitalRateToday)
                      : "2800"
                  }
                  className={inputClass}
                  disabled={acting}
                />
              </FormField>
              <button
                type="button"
                onClick={() => void handleApplyExchangeRate()}
                disabled={acting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green px-4 py-2.5 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                {invoice.exchangeRateCdf != null && invoice.exchangeRateCdf > 0
                  ? "Mettre à jour le taux"
                  : "Appliquer le taux sur la facture"}
              </button>
              <p className="text-[11px] text-muted">
                Laissez vide pour utiliser le taux du jour de l&apos;hôpital.
              </p>
              {rateMessage && <p className="text-xs text-green-700">{rateMessage}</p>}
            </div>
          )}

          {scope === "auditor" && (canStartReview || canDecide) && (
            <div className="theme-card h-fit space-y-4 p-5">
              <h2 className="text-base font-semibold text-foreground">Décision d&apos;audit</h2>
              {canDecide && workflow ? (
                <div
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    workflow.state === "anomaly"
                      ? "border-red-200 bg-red-50 text-red-900"
                      : workflow.state === "controlled"
                        ? "border-green-200 bg-green-50 text-green-900"
                        : "border-primary/20 bg-primary-soft/40 text-foreground"
                  }`}
                >
                  {workflow.state === "coding" && "1. Codez les actes par patient, puis transmettez la facture au Sinistre."}
                  {workflow.state === "pending" && "2. Transmise : attente Sinistre. Le Sinistre contrôle et valide la facture."}
                  {workflow.state === "controlled" && "3. Validée par le Sinistre."}
                  {workflow.state === "anomaly" && (
                    <>
                      <strong>Anomalie signalée par le Sinistre :</strong> {workflow.claims_notes || "—"}
                      <br />
                      Corrigez la codification puis retransmettez la facture.
                    </>
                  )}
                </div>
              ) : null}
              <FormField
                label="Note de synthèse"
                hint="Obligatoire pour transmettre au Sinistre. Conformité médicale, réfactions et points d'attention."
                required
              >
                <textarea
                  id="audit-synthesis-note"
                  value={auditNotes}
                  onChange={(event) => setAuditNotes(event.target.value)}
                  className={`${inputClass} min-h-[80px]`}
                  disabled={acting}
                />
              </FormField>
              {canStartReview && (
                <button
                  type="button"
                  onClick={() => void handleStartReview()}
                  disabled={acting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                >
                  <PlayCircle size={16} />
                  Démarrer l&apos;examen
                </button>
              )}
              {canDecide && (
                <>
                  {workflow?.editable ? (
                    <button
                      type="button"
                      onClick={() => void handleTransmit()}
                      disabled={acting || transmitting}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                    >
                      <Send size={16} />
                      {workflow.state === "anomaly" ? "Retransmettre au Sinistre" : "Transmettre au Sinistre"}
                    </button>
                  ) : null}
                  <FormField label="Raison du rejet">
                    <textarea
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      className={`${inputClass} min-h-[70px]`}
                      disabled={acting}
                      placeholder="Obligatoire pour rejeter"
                    />
                  </FormField>
                  <button
                    type="button"
                    onClick={() => void handleReject()}
                    disabled={acting || !rejectionReason.trim()}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-600/90 disabled:opacity-60"
                  >
                    <XCircle size={16} />
                    Rejeter
                  </button>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </>
  );
}
